import styled from 'styled-components';
import { motion } from 'framer-motion';

export const TimelineContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 0.5rem 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 20px;
    width: 2px;
    height: 100%;
    background: linear-gradient(
      to bottom,
      transparent,
      var(--accent-color) 10%,
      var(--accent-color) 90%,
      transparent
    );
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    padding: 0.25rem 0;
  }
`;

export const TimelineItem = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  padding-left: 40px;

  &:last-child {
    margin-bottom: 0;
  }

  .timeline-dot {
    position: absolute;
    left: 20px;
    width: 12px;
    height: 12px;
    background: var(--bg-primary);
    border: 2px solid var(--accent-color);
    border-radius: 50%;
    transform: translateX(-50%);
    z-index: 2;
    transition: all 0.3s ease;
    box-shadow: 0 0 10px var(--accent-color);
  }

  &:hover .timeline-dot {
    transform: translateX(-50%) scale(1.2);
    box-shadow: 0 0 15px var(--accent-color);
  }
`;

export const TimelineContent = styled.div<{ isMinimized?: boolean }>`
  width: 100%;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  position: relative;
  will-change: transform, opacity;
  margin: 0.25rem 0;

  &::before {
    content: 'Hover to expand';
    position: absolute;
    top: -12px;
    right: 10px;
    background: var(--accent-color);
    color: var(--bg-primary);
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.7rem;
    opacity: 1;
    transform: translateY(0);
    transition: all 0.3s ease;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: var(--accent-color);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

    &::before {
      opacity: 0;
      transform: translateY(5px);
    }
  }

  .journey-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    transition: margin-bottom 0.3s ease;

    h3 {
      color: var(--accent-color);
      font-size: 1.1rem;
      margin: 0;
      font-weight: 600;
      transition: color 0.3s ease;
    }

    .journey-date {
      color: var(--text-secondary);
      font-size: 0.9rem;
      opacity: 0.8;
      white-space: nowrap;
      transition: opacity 0.3s ease;
    }
  }

  .journey-tagline {
    color: var(--text-primary);
    font-size: 0.95rem;
    margin-bottom: 0.75rem;
    line-height: 1.4;
    transition: all 0.3s ease;
    transform-origin: top;
  }

  .journey-details {
    max-height: 0;
    overflow: hidden;
    transition: all 0.3s ease-out;
    margin-bottom: 0;
    transform-origin: top;

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      
      li {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
        color: var(--text-secondary);
        font-size: 0.9rem;
        line-height: 1.4;
        
        .bullet {
          color: var(--accent-color);
          font-size: 1rem;
          line-height: 1;
          margin-top: 0.2rem;
        }
      }
    }
  }

  &:hover .journey-details {
    max-height: 500px;
  }

  .journey-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    transition: all 0.3s ease;
    transform-origin: top;

    .tag {
      padding: 0.3rem 0.6rem;
      background: rgba(100, 255, 218, 0.1);
      color: var(--accent-color);
      border-radius: 15px;
      font-size: 0.8rem;
      transition: all 0.2s ease;
      font-weight: 500;

      &:hover {
        background: rgba(100, 255, 218, 0.2);
        transform: translateY(-1px);
      }
    }
  }

  ${({ isMinimized }) => isMinimized && `
    padding: 0.75rem;
    margin: 0.25rem 0;
    
    .journey-header {
      margin-bottom: 0;
      
      .journey-date {
        opacity: 0;
      }
    }
    
    .journey-tagline,
    .journey-details,
    .journey-tags {
      opacity: 0;
      max-height: 0;
      margin: 0;
      padding: 0;
      overflow: hidden;
      transform: scaleY(0);
    }
  `}

  @media (max-width: 768px) {
    padding: 0.75rem;
    margin: 0.15rem 0;

    .journey-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
    }

    .journey-details {
      max-height: none;
    }

    &::before {
      display: none;
    }
  }
`; 